import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { inrFull, fdt } from "@/lib/format";
import { Receipt, AlertTriangle } from "lucide-react";

const LABEL = { not_billed: ["Not yet billed", "bg-amber-100 text-amber-800"], partially_billed: ["Partially billed", "bg-sky-100 text-sky-800"], billed: ["Billed", "bg-emerald-100 text-emerald-800"] };

function Row({ it }) {
  const navigate = useNavigate();
  const [label, tone] = LABEL[it.billing_status] || LABEL.not_billed;
  return (
    <div className="rounded-xl border border-slate-100 p-3" data-testid={`consult-bill-${it.encounter_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{it.pet_name}<span className="font-normal text-slate-500"> · {it.customer_name}{it.customer_mobile ? ` · ${it.customer_mobile}` : ""}</span></p>
          <p className="text-xs text-slate-500">Completed {fdt(it.completed_at)}{it.veterinarian ? ` · ${it.veterinarian}` : ""}</p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`} data-testid={`consult-bill-status-${it.encounter_id}`}>{label}</span>
      </div>
      <div className="mt-2 grid gap-2 text-xs sm:grid-cols-2">
        <div><p className="font-semibold text-slate-600">To bill</p>
          {it.services.map((s) => <p key={s.service_id} className="text-slate-700">{s.service_name}{s.price != null ? ` · ${inrFull(s.price)}` : ""}</p>)}
          {it.dispensed.map((d) => <p key={d.product_id} className="text-slate-700" data-testid={`consult-bill-med-${d.product_id}`}>{d.name} × {d.qty}{d.price != null ? ` · ${inrFull(d.price)} each` : ""}{d.billed_qty ? ` (${d.billed_qty} billed)` : ""}
            {d.insufficient_stock && <span className="ml-1 inline-flex items-center gap-0.5 text-rose-600"><AlertTriangle className="h-3 w-3" />low stock ({d.stock})</span>}</p>)}
          {!it.services.length && !it.dispensed.length && <p className="text-slate-400">No dispensed items</p>}
          {it.needs_manual_fee && <p className="mt-1 inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 font-semibold text-amber-800" data-testid={`consult-bill-manual-${it.encounter_id}`}><AlertTriangle className="h-3 w-3" />Needs manual fee: add the consultation service in POS</p>}
        </div>
        <div><p className="font-semibold text-slate-600">Prescription notes (not charged)</p>
          {it.prescriptions.filter((p) => p.dispense_status !== "dispensed").map((p, i) => <p key={i} className="text-slate-500">{p.medication_name}{p.dosage ? ` · ${p.dosage}` : ""}{p.frequency ? ` · ${p.frequency}` : ""}</p>)}
          {!it.prescriptions.some((p) => p.dispense_status !== "dispensed") && <p className="text-slate-400">None</p>}
        </div>
      </div>
      {it.billing_status !== "billed" && (
        <button onClick={() => navigate(`/pos?encounter=${it.encounter_id}`)} data-testid={`consult-bill-open-${it.encounter_id}`}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"><Receipt className="h-3.5 w-3.5" /> Review bill</button>
      )}
    </div>
  );
}

export function ConsultBillingQueue() {
  const { data, isLoading, isError } = useQuery({ queryKey: ["consult-billing-queue"], refetchInterval: 60000,
    queryFn: async () => (await api.get("/veterinary/billing-queue")).data, retry: false });
  if (isError) return null;
  const items = data?.items || [];
  return (
    <div className="rounded-2xl border border-pawkt-border bg-white p-4" data-testid="consult-billing-queue">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-display font-bold text-pawkt-text"><Receipt className="h-4 w-4 text-violet-500" /> Consultations awaiting billing</p>
        <span className="text-xs text-slate-500" data-testid="consult-billing-counts">{data ? `${data.counts.not_billed} not billed · ${data.counts.partially_billed} partially billed` : ""}</span>
      </div>
      {isLoading ? <p className="text-sm text-slate-400">Loading…</p> : !items.length
        ? <p className="text-sm text-pawkt-text-secondary" data-testid="consult-billing-empty">No completed consultations waiting for billing.</p>
        : <div className="space-y-2">{items.map((it) => <Row key={it.encounter_id} it={it} />)}</div>}
    </div>
  );
}
